import { atom, getDefaultStore } from 'jotai'

import { api, FieldError } from '../lib/api'
import { getConfig } from '../lib/config'
import { getItem, setItem, removeItem, STAFF_AUTH_TOKEN_KEY } from '../lib/storage'

export const Permission = {
  StaffSessionRead: 'staff.session.read',
  StaffSessionRevoke: 'staff.session.revoke',
  StaffStatusRead: 'staff.status.read',
  ModerationPhotosRead: 'moderation.photos.read',
  ModerationPhotosWrite: 'moderation.photos.write',
  ModerationReportsRead: 'moderation.reports.read',
  ModerationReportsWrite: 'moderation.reports.write',
  ModerationUsersRead: 'moderation.users.read',
  ModerationUsersWrite: 'moderation.users.write',
  SettingsRegionsRead: 'settings.regions.read',
  SettingsRegionsWrite: 'settings.regions.write',
  SettingsWaitlistRead: 'settings.waitlist.read',
  SettingsWaitlistWrite: 'settings.waitlist.write',
  SettingsStaffRead: 'settings.staff.read',
  SettingsStaffWrite: 'settings.staff.write',
} as const

export type StaffPermission = (typeof Permission)[keyof typeof Permission] | string

export type StaffMember = {
  id: string
  email: string
  name: string
  roleId: string
  roleName?: string
  permissions?: string[]
  isActive: boolean
  createdAt: string
  updatedAt?: string
}

export type StaffToken = {
  id: string
  staffId: string
  value: string
  isActive: boolean
  expiresAt: string
  createdAt: string
  lastUsedAt?: string
}

export type StaffSession = {
  staff: StaffMember
  token: StaffToken
}

export type StaffSessionStatus = 'unknown' | 'authenticated' | 'unauthenticated'

export type StaffStatus = {
  users: { active: number; total: number }
  profiles: { visible: number; total: number }
  matches: { active: number; connected: number }
  waitlist: { pending: number; notified: number; converted: number }
  moderation: { pendingPhotos: number; openReports: number }
  regions: { active: number; inactive: number }
}

export type StaffPhoto = {
  id: string
  profileId: string
  userId: string
  status: string
  contentAnalysis?: Record<string, unknown>
  moderationFlags?: string[]
  attractivenessScore?: number
  faces?: { boundingBox: number[]; confidence: number }[]
  processedAt?: string
  createdAt: string
  updatedAt?: string
}

/** Staff-controlled QA matching visibility for a member. */
export type TestMatchMode = 'disabled' | 'onlySimulated' | 'simulatedAndReal'

export type StaffUserRow = {
  id: string
  email: string
  isActive: boolean
  isVerified: boolean
  subscriptionTier: string
  subscriptionStatus: string
  testMatchMode?: TestMatchMode
  name?: string
  location?: string
  profileId?: string
  photoPending: number
  photoApproved: number
  photoRejected: number
  openReports: number
  rejectionType?: string
  rejectionReason?: string
  createdAt: string
}

export type StaffReport = {
  id: string
  reporterUserId: string
  reportedUserId: string
  reportedProfileId: string
  reason: string
  details?: string
  status: string
  createdAt: string
}

export type StaffUserDetail = {
  user: StaffUserRow
  profile?: Record<string, unknown>
  photos: StaffPhoto[]
  reports: StaffReport[]
}

export type PhotoQuery = {
  q: string
  status: string
  sort: string
  order: 'asc' | 'desc'
}

export type UserQuery = {
  q: string
  sort: string
  order: 'asc' | 'desc'
}

type ErrorResponse = {
  error?: string
  fields?: FieldError[]
}

export const staffSessionAtom = atom<StaffSession | null>(null)
export const staffSessionStatusAtom = atom<StaffSessionStatus>('unknown')
export const staffLoginIsLoadingAtom = atom(false)
export const staffLoginIssuesAtom = atom<FieldError[]>([])

export const staffStatusAtom = atom<StaffStatus | null>(null)
export const staffStatusIsLoadingAtom = atom(false)
export const staffStatusErrorAtom = atom<string | null>(null)

export const staffPhotosAtom = atom<StaffPhoto[]>([])
export const staffPhotosTotalAtom = atom(0)
export const staffPhotosIsLoadingAtom = atom(false)
export const staffPhotosErrorAtom = atom<string | null>(null)
export const staffPhotoQueryAtom = atom<PhotoQuery>({
  q: '',
  status: 'pending',
  sort: 'createdAt',
  order: 'desc',
})

export const staffUsersAtom = atom<StaffUserRow[]>([])
export const staffUsersTotalAtom = atom(0)
export const staffUsersIsLoadingAtom = atom(false)
export const staffUsersErrorAtom = atom<string | null>(null)
export const staffUserQueryAtom = atom<UserQuery>({
  q: '',
  sort: 'createdAt',
  order: 'desc',
})

export const staffSelectedUserAtom = atom<StaffUserDetail | null>(null)
export const staffSelectedUserIsLoadingAtom = atom(false)
export const staffSelectedUserErrorAtom = atom<string | null>(null)

export const staffActionIsLoadingAtom = atom(false)
export const staffActionErrorAtom = atom<string | null>(null)

async function getStaffTokenValue() {
  return getItem(STAFF_AUTH_TOKEN_KEY) ?? ''
}

export async function staffAuthHeaders(extra?: HeadersInit): Promise<HeadersInit> {
  const token = await getStaffTokenValue()
  return {
    ...(extra || {}),
    Authorization: token ? `Bearer ${token}` : '',
  }
}

export function hasStaffPermission(
  session: StaffSession | null | undefined,
  permission: StaffPermission,
) {
  if (!session?.staff?.permissions) return false
  for (const granted of session.staff.permissions) {
    if (granted === '*' || granted === permission) return true
  }
  return false
}

async function clearStaffSession(set: (atom: any, value: any) => void) {
  removeItem(STAFF_AUTH_TOKEN_KEY)
  set(staffSessionAtom, null)
  set(staffSessionStatusAtom, 'unauthenticated')
}

async function handleAuthFailure(response: Response, set: (atom: any, value: any) => void) {
  if (response.status === 401) {
    await clearStaffSession(set)
    return true
  }
  return false
}

export const staffLoginAtom = atom(
  null,
  async function staffLogin(_get, set, credentials: { email: string; password: string }) {
    set(staffLoginIsLoadingAtom, true)
    set(staffLoginIssuesAtom, [])

    try {
      const response = await api.fetch('/staff/tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      })

      if (!response.ok) {
        let data: ErrorResponse = {}
        try {
          data = (await response.json()) as ErrorResponse
        } catch {
          // non-JSON body
        }
        set(staffLoginIsLoadingAtom, false)
        set(staffLoginIssuesAtom, data.fields ?? [{ password: 'invalidCredentials' }])
        set(staffSessionStatusAtom, 'unauthenticated')
        return
      }

      const data = (await response.json()) as StaffSession
      setItem(STAFF_AUTH_TOKEN_KEY, data.token.value)
      set(staffSessionAtom, data)
      set(staffSessionStatusAtom, 'authenticated')
      set(staffLoginIsLoadingAtom, false)
    } catch {
      set(staffLoginIsLoadingAtom, false)
      set(staffLoginIssuesAtom, [{ email: 'networkError' }])
      set(staffSessionStatusAtom, 'unauthenticated')
    }
  },
)

export const staffLogoutAtom = atom(null, async function staffLogout(get, set) {
  const current = get(staffSessionAtom)
  if (current) {
    try {
      await api.fetch('/staff/tokens/revoke', {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })
    } catch {
      // still clear local session
    }
  }

  removeItem(STAFF_AUTH_TOKEN_KEY)
  set(staffSessionAtom, null)
  set(staffSessionStatusAtom, 'unauthenticated')
  set(staffStatusAtom, null)
  set(staffPhotosAtom, [])
  set(staffUsersAtom, [])
  set(staffSelectedUserAtom, null)
})

export const staffRestoreAtom = atom(null, async function staffRestore(_get, set) {
  const token = getItem(STAFF_AUTH_TOKEN_KEY)
  if (!token) {
    set(staffSessionStatusAtom, 'unauthenticated')
    return
  }

  const response = await api.fetch('/staff/session', {
    method: 'GET',
    headers: await staffAuthHeaders(),
  })

  if (!response.ok) {
    await clearStaffSession(set)
    return
  }

  const data = (await response.json()) as StaffSession
  set(staffSessionAtom, data)
  set(staffSessionStatusAtom, 'authenticated')
})

export async function restore() {
  await getDefaultStore().set(staffRestoreAtom)
}

export const fetchStaffStatusAtom = atom(null, async function fetchStaffStatus(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.StaffStatusRead)) {
    set(staffStatusErrorAtom, 'forbidden')
    set(staffStatusAtom, null)
    return
  }

  set(staffStatusIsLoadingAtom, true)
  set(staffStatusErrorAtom, null)

  try {
    const response = await api.fetch('/staff/status', {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffStatusIsLoadingAtom, false)
      set(staffStatusErrorAtom, 'unauthorized')
      return
    }

    if (response.status === 403) {
      set(staffStatusIsLoadingAtom, false)
      set(staffStatusErrorAtom, 'forbidden')
      set(staffStatusAtom, null)
      return
    }

    if (!response.ok) {
      set(staffStatusIsLoadingAtom, false)
      set(staffStatusErrorAtom, 'failed')
      return
    }

    const data = (await response.json()) as StaffStatus
    set(staffStatusAtom, data)
    set(staffStatusIsLoadingAtom, false)
  } catch {
    set(staffStatusIsLoadingAtom, false)
    set(staffStatusErrorAtom, 'network')
  }
})

export const fetchStaffPhotosAtom = atom(null, async function fetchStaffPhotos(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationPhotosRead)) {
    set(staffPhotosErrorAtom, 'forbidden')
    set(staffPhotosAtom, [])
    return
  }

  const query = get(staffPhotoQueryAtom)
  set(staffPhotosIsLoadingAtom, true)
  set(staffPhotosErrorAtom, null)

  const params = new URLSearchParams()
  if (query.q) params.set('q', query.q)
  if (query.status) params.set('status', query.status)
  params.set('sort', query.sort)
  params.set('order', query.order)
  params.set('limit', '50')

  try {
    const response = await api.fetch(`/staff/moderation/photos?${params.toString()}`, {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffPhotosIsLoadingAtom, false)
      set(staffPhotosErrorAtom, 'unauthorized')
      return
    }

    if (response.status === 403) {
      set(staffPhotosIsLoadingAtom, false)
      set(staffPhotosErrorAtom, 'forbidden')
      return
    }

    if (!response.ok) {
      set(staffPhotosIsLoadingAtom, false)
      set(staffPhotosErrorAtom, 'failed')
      return
    }

    const data = (await response.json()) as { photos: StaffPhoto[]; total: number }
    set(staffPhotosAtom, data.photos ?? [])
    set(staffPhotosTotalAtom, data.total ?? 0)
    set(staffPhotosIsLoadingAtom, false)
  } catch {
    set(staffPhotosIsLoadingAtom, false)
    set(staffPhotosErrorAtom, 'network')
  }
})

export const setStaffPhotoQueryAtom = atom(
  null,
  function setStaffPhotoQuery(_get, set, patch: Partial<PhotoQuery>) {
    set(staffPhotoQueryAtom, (current) => ({ ...current, ...patch }))
  },
)

export const fetchStaffUsersAtom = atom(null, async function fetchStaffUsers(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationUsersRead)) {
    set(staffUsersErrorAtom, 'forbidden')
    set(staffUsersAtom, [])
    return
  }

  const query = get(staffUserQueryAtom)
  set(staffUsersIsLoadingAtom, true)
  set(staffUsersErrorAtom, null)

  const params = new URLSearchParams()
  if (query.q) params.set('q', query.q)
  params.set('sort', query.sort)
  params.set('order', query.order)
  params.set('limit', '50')

  try {
    const response = await api.fetch(`/staff/moderation/users?${params.toString()}`, {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffUsersIsLoadingAtom, false)
      set(staffUsersErrorAtom, 'unauthorized')
      return
    }

    if (response.status === 403) {
      set(staffUsersIsLoadingAtom, false)
      set(staffUsersErrorAtom, 'forbidden')
      return
    }

    if (!response.ok) {
      set(staffUsersIsLoadingAtom, false)
      set(staffUsersErrorAtom, 'failed')
      return
    }

    const data = (await response.json()) as { users: StaffUserRow[]; total: number }
    set(staffUsersAtom, data.users ?? [])
    set(staffUsersTotalAtom, data.total ?? 0)
    set(staffUsersIsLoadingAtom, false)
  } catch {
    set(staffUsersIsLoadingAtom, false)
    set(staffUsersErrorAtom, 'network')
  }
})

export const setStaffUserQueryAtom = atom(
  null,
  function setStaffUserQuery(_get, set, patch: Partial<UserQuery>) {
    set(staffUserQueryAtom, (current) => ({ ...current, ...patch }))
  },
)

export const fetchStaffUserDetailAtom = atom(
  null,
  async function fetchStaffUserDetail(get, set, userId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationUsersRead)) {
      set(staffSelectedUserErrorAtom, 'forbidden')
      return
    }

    set(staffSelectedUserIsLoadingAtom, true)
    set(staffSelectedUserErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/users/${userId}`, {
        method: 'GET',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffSelectedUserIsLoadingAtom, false)
        set(staffSelectedUserErrorAtom, 'unauthorized')
        return
      }

      if (!response.ok) {
        set(staffSelectedUserIsLoadingAtom, false)
        set(staffSelectedUserErrorAtom, response.status === 404 ? 'notFound' : 'failed')
        return
      }

      const data = (await response.json()) as StaffUserDetail
      set(staffSelectedUserAtom, data)
      set(staffSelectedUserIsLoadingAtom, false)
    } catch {
      set(staffSelectedUserIsLoadingAtom, false)
      set(staffSelectedUserErrorAtom, 'network')
    }
  },
)

export const approveStaffPhotoAtom = atom(
  null,
  async function approveStaffPhoto(get, set, photoId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationPhotosWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/photos/${photoId}/approve`, {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const photo = (await response.json()) as StaffPhoto
      set(staffPhotosAtom, (photos) => photos.map((item) => (item.id === photoId ? photo : item)))
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const rejectStaffPhotoAtom = atom(
  null,
  async function rejectStaffPhoto(get, set, photoId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationPhotosWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/photos/${photoId}/reject`, {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const photo = (await response.json()) as StaffPhoto
      set(staffPhotosAtom, (photos) => photos.map((item) => (item.id === photoId ? photo : item)))
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const setStaffUserTestMatchModeAtom = atom(
  null,
  async function setStaffUserTestMatchMode(
    get,
    set,
    payload: { userId: string; testMatchMode: TestMatchMode },
  ) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationUsersWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(
        `/staff/moderation/users/${payload.userId}/test-match-mode`,
        {
          method: 'POST',
          headers: {
            ...(await staffAuthHeaders({ 'Content-Type': 'application/json' })),
          },
          body: JSON.stringify({ testMatchMode: payload.testMatchMode }),
        },
      )

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const user = (await response.json()) as StaffUserRow
      set(staffUsersAtom, (users) =>
        users.map((item) => (item.id === payload.userId ? user : item)),
      )
      const selected = get(staffSelectedUserAtom)
      if (selected?.user.id === payload.userId) {
        set(staffSelectedUserAtom, { ...selected, user })
      }
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const banStaffUserAtom = atom(
  null,
  async function banStaffUser(get, set, payload: { userId: string; reason?: string }) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationUsersWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/users/${payload.userId}/ban`, {
        method: 'POST',
        headers: {
          ...(await staffAuthHeaders({ 'Content-Type': 'application/json' })),
        },
        body: JSON.stringify({ reason: payload.reason ?? '' }),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const user = (await response.json()) as StaffUserRow
      set(staffUsersAtom, (users) =>
        users.map((item) => (item.id === payload.userId ? user : item)),
      )
      const selected = get(staffSelectedUserAtom)
      if (selected?.user.id === payload.userId) {
        set(staffSelectedUserAtom, { ...selected, user })
      }
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export type StaffRegion = {
  id: string
  location: string
  country: string
  osmId: number
  osmType: string
  radiusKm: number
  isEnabled: boolean
  isActive: boolean
  forceActivate: boolean
  activatedAt?: string
  waitlistCount: number
  createdAt: string
  updatedAt?: string
}

export type StaffWaitlistEntry = {
  id: string
  email: string
  location: string
  country: string
  notifiedAt?: string
  convertedAt?: string
  createdAt: string
}

export type StaffAdminMember = {
  id: string
  email: string
  name: string
  roleId: string
  roleName?: string
  isActive: boolean
  createdAt: string
}

export type StaffAdminRole = {
  id: string
  name: string
  permissions: string[]
  createdAt: string
}

export const staffReportsAtom = atom<StaffReport[]>([])
export const staffReportsIsLoadingAtom = atom(false)
export const staffReportsErrorAtom = atom<string | null>(null)

export const staffRegionsAtom = atom<StaffRegion[]>([])
export const staffRegionsThresholdAtom = atom(80)
export const staffRegionsIsLoadingAtom = atom(false)
export const staffRegionsErrorAtom = atom<string | null>(null)

export const staffWaitlistAtom = atom<StaffWaitlistEntry[]>([])
export const staffWaitlistTotalAtom = atom(0)
export const staffWaitlistIsLoadingAtom = atom(false)
export const staffWaitlistErrorAtom = atom<string | null>(null)
export const staffWaitlistStatusAtom = atom('pending')

export const staffAdminStaffAtom = atom<StaffAdminMember[]>([])
export const staffAdminRolesAtom = atom<StaffAdminRole[]>([])
export const staffAdminIsLoadingAtom = atom(false)
export const staffAdminErrorAtom = atom<string | null>(null)

export const fetchStaffReportsAtom = atom(null, async function fetchStaffReports(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationReportsRead)) {
    set(staffReportsErrorAtom, 'forbidden')
    set(staffReportsAtom, [])
    return
  }

  set(staffReportsIsLoadingAtom, true)
  set(staffReportsErrorAtom, null)

  try {
    const response = await api.fetch('/staff/moderation/reports', {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffReportsIsLoadingAtom, false)
      set(staffReportsErrorAtom, 'unauthorized')
      return
    }

    if (!response.ok) {
      set(staffReportsIsLoadingAtom, false)
      set(staffReportsErrorAtom, response.status === 403 ? 'forbidden' : 'failed')
      return
    }

    const data = (await response.json()) as { reports: StaffReport[] }
    set(staffReportsAtom, data.reports ?? [])
    set(staffReportsIsLoadingAtom, false)
  } catch {
    set(staffReportsIsLoadingAtom, false)
    set(staffReportsErrorAtom, 'network')
  }
})

export const resolveStaffReportAtom = atom(
  null,
  async function resolveStaffReport(get, set, reportId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationReportsWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/reports/${reportId}/resolve`, {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      set(staffReportsAtom, (reports) => reports.filter((report) => report.id !== reportId))
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const unbanStaffUserAtom = atom(
  null,
  async function unbanStaffUser(get, set, userId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.ModerationUsersWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/moderation/users/${userId}/unban`, {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const user = (await response.json()) as StaffUserRow
      set(staffUsersAtom, (users) => users.map((item) => (item.id === userId ? user : item)))
      const selected = get(staffSelectedUserAtom)
      if (selected?.user.id === userId) {
        set(staffSelectedUserAtom, { ...selected, user })
      }
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const fetchStaffRegionsAtom = atom(null, async function fetchStaffRegions(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsRegionsRead)) {
    set(staffRegionsErrorAtom, 'forbidden')
    set(staffRegionsAtom, [])
    return
  }

  set(staffRegionsIsLoadingAtom, true)
  set(staffRegionsErrorAtom, null)

  try {
    const response = await api.fetch('/staff/settings/regions', {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffRegionsIsLoadingAtom, false)
      set(staffRegionsErrorAtom, 'unauthorized')
      return
    }

    if (!response.ok) {
      set(staffRegionsIsLoadingAtom, false)
      set(staffRegionsErrorAtom, response.status === 403 ? 'forbidden' : 'failed')
      return
    }

    const data = (await response.json()) as {
      regions: StaffRegion[]
      activationThreshold: number
    }
    set(staffRegionsAtom, data.regions ?? [])
    set(staffRegionsThresholdAtom, data.activationThreshold ?? 80)
    set(staffRegionsIsLoadingAtom, false)
  } catch {
    set(staffRegionsIsLoadingAtom, false)
    set(staffRegionsErrorAtom, 'network')
  }
})

export const patchStaffRegionAtom = atom(
  null,
  async function patchStaffRegion(
    get,
    set,
    payload: {
      regionId: string
      isEnabled?: boolean
      forceActivate?: boolean
      activate?: boolean
    },
  ) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsRegionsWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const body: {
        isEnabled?: boolean
        forceActivate?: boolean
        activate?: boolean
      } = {}
      if (payload.isEnabled !== undefined) body.isEnabled = payload.isEnabled
      if (payload.forceActivate !== undefined) body.forceActivate = payload.forceActivate
      if (payload.activate !== undefined) body.activate = payload.activate

      const response = await api.fetch(`/staff/settings/regions/${payload.regionId}`, {
        method: 'PATCH',
        headers: await staffAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const region = (await response.json()) as StaffRegion
      set(staffRegionsAtom, (regions) =>
        regions.map((item) => (item.id === payload.regionId ? { ...item, ...region } : item)),
      )
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const fetchStaffWaitlistAtom = atom(null, async function fetchStaffWaitlist(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsWaitlistRead)) {
    set(staffWaitlistErrorAtom, 'forbidden')
    set(staffWaitlistAtom, [])
    return
  }

  const status = get(staffWaitlistStatusAtom)
  set(staffWaitlistIsLoadingAtom, true)
  set(staffWaitlistErrorAtom, null)

  const params = new URLSearchParams()
  if (status && status !== 'all') params.set('status', status)
  params.set('limit', '100')

  try {
    const response = await api.fetch(`/staff/settings/waitlist?${params.toString()}`, {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffWaitlistIsLoadingAtom, false)
      set(staffWaitlistErrorAtom, 'unauthorized')
      return
    }

    if (!response.ok) {
      set(staffWaitlistIsLoadingAtom, false)
      set(staffWaitlistErrorAtom, response.status === 403 ? 'forbidden' : 'failed')
      return
    }

    const data = (await response.json()) as {
      entries: StaffWaitlistEntry[]
      total: number
    }
    set(staffWaitlistAtom, data.entries ?? [])
    set(staffWaitlistTotalAtom, data.total ?? 0)
    set(staffWaitlistIsLoadingAtom, false)
  } catch {
    set(staffWaitlistIsLoadingAtom, false)
    set(staffWaitlistErrorAtom, 'network')
  }
})

export const notifyStaffWaitlistAtom = atom(
  null,
  async function notifyStaffWaitlist(get, set, entryId: string) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsWaitlistWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch(`/staff/settings/waitlist/${entryId}/notify`, {
        method: 'POST',
        headers: await staffAuthHeaders(),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const entry = (await response.json()) as StaffWaitlistEntry
      set(staffWaitlistAtom, (entries) =>
        entries.map((item) => (item.id === entryId ? entry : item)),
      )
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const fetchStaffAdminAtom = atom(null, async function fetchStaffAdmin(get, set) {
  if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsStaffRead)) {
    set(staffAdminErrorAtom, 'forbidden')
    set(staffAdminStaffAtom, [])
    set(staffAdminRolesAtom, [])
    return
  }

  set(staffAdminIsLoadingAtom, true)
  set(staffAdminErrorAtom, null)

  try {
    const response = await api.fetch('/staff/settings/staff', {
      method: 'GET',
      headers: await staffAuthHeaders(),
    })

    if (await handleAuthFailure(response, set)) {
      set(staffAdminIsLoadingAtom, false)
      set(staffAdminErrorAtom, 'unauthorized')
      return
    }

    if (!response.ok) {
      set(staffAdminIsLoadingAtom, false)
      set(staffAdminErrorAtom, response.status === 403 ? 'forbidden' : 'failed')
      return
    }

    const data = (await response.json()) as {
      staff: StaffAdminMember[]
      roles: StaffAdminRole[]
    }
    set(staffAdminStaffAtom, data.staff ?? [])
    set(staffAdminRolesAtom, data.roles ?? [])
    set(staffAdminIsLoadingAtom, false)
  } catch {
    set(staffAdminIsLoadingAtom, false)
    set(staffAdminErrorAtom, 'network')
  }
})

export const createStaffMemberAtom = atom(
  null,
  async function createStaffMember(
    get,
    set,
    payload: { email: string; name: string; password: string; roleId: string },
  ) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsStaffWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch('/staff/settings/staff', {
        method: 'POST',
        headers: await staffAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, 'failed')
        return { success: false as const }
      }

      const member = (await response.json()) as StaffAdminMember
      set(staffAdminStaffAtom, (list) => [...list, member])
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

export const updateStaffRoleAtom = atom(
  null,
  async function updateStaffRole(
    get,
    set,
    payload: { roleId: string; name?: string; permissions: string[] },
  ) {
    if (!hasStaffPermission(get(staffSessionAtom), Permission.SettingsStaffWrite)) {
      set(staffActionErrorAtom, 'forbidden')
      return { success: false as const, error: 'forbidden' as const }
    }

    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const body: { permissions: string[]; name?: string } = {
        permissions: payload.permissions,
      }
      if (payload.name !== undefined) body.name = payload.name

      const response = await api.fetch(`/staff/settings/roles/${payload.roleId}`, {
        method: 'PATCH',
        headers: await staffAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const, error: 'unauthorized' as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        const error = response.status === 409 ? ('nameAlreadyInUse' as const) : ('failed' as const)
        set(staffActionErrorAtom, error)
        return { success: false as const, error }
      }

      const role = (await response.json()) as StaffAdminRole
      set(staffAdminRolesAtom, (roles) =>
        roles.map((item) => (item.id === payload.roleId ? role : item)),
      )
      // Keep staff member list role names in sync after rename.
      set(staffAdminStaffAtom, (members) =>
        members.map((member) =>
          member.roleId === payload.roleId ? { ...member, roleName: role.name } : member,
        ),
      )
      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const, error: 'network' as const }
    }
  },
)

export const changeStaffPasswordAtom = atom(
  null,
  async function changeStaffPassword(
    _get,
    set,
    payload: { currentPassword: string; newPassword: string },
  ) {
    set(staffActionIsLoadingAtom, true)
    set(staffActionErrorAtom, null)

    try {
      const response = await api.fetch('/staff/settings/account/password', {
        method: 'POST',
        headers: await staffAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      })

      if (await handleAuthFailure(response, set)) {
        set(staffActionIsLoadingAtom, false)
        return { success: false as const }
      }

      if (!response.ok) {
        set(staffActionIsLoadingAtom, false)
        set(staffActionErrorAtom, response.status === 401 ? 'invalidCredentials' : 'failed')
        return { success: false as const }
      }

      set(staffActionIsLoadingAtom, false)
      return { success: true as const }
    } catch {
      set(staffActionIsLoadingAtom, false)
      set(staffActionErrorAtom, 'network')
      return { success: false as const }
    }
  },
)

/** Fetch a staff-moderated photo as an object URL (Bearer auth cannot go on <img src>). */
export async function fetchStaffPhotoObjectUrl(photoId: string, size = 400) {
  const baseUrl = getConfig().apiUrl
  const url = new URL(`/staff/moderation/photos/${photoId}/serve?size=${size}`, baseUrl)
  const response = await fetch(url, {
    headers: (await staffAuthHeaders()) as HeadersInit,
  })
  if (!response.ok) return null
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

export function photoDescription(photo: StaffPhoto) {
  const analysis = photo.contentAnalysis
  if (!analysis) return ''
  const description = analysis['description']
  return typeof description === 'string' ? description : ''
}

export function photoFeatures(photo: StaffPhoto) {
  const features = photo.contentAnalysis?.['features']
  if (!Array.isArray(features)) return [] as string[]
  return features.filter((item): item is string => typeof item === 'string')
}

export function statusTagColor(status: string) {
  if (status === 'approved') return 'teal' as const
  if (status === 'rejected') return 'red' as const
  if (status === 'pending' || status === 'processing') return 'amber' as const
  return 'violet' as const
}
